import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Bar, Icon, EmptyState, useToast } from '@/components';
import { useCourse, useLessonQuiz, useCompleteLesson, useSubmitLessonQuiz } from '@/hooks/queries';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useLocalized } from '@/hooks/useLocalized';
import { getSupabase } from '@/lib/supabase';
import { QuizQuestionView } from './QuizQuestionView';
import type { Lesson, LessonQuizResult, QuizAnswers, QuizQuestion } from '@/types/domain';

type LessonMediaKind = 'pdf' | 'image' | 'html';
type LessonMediaContent = {
  type: LessonMediaKind;
  files: string[];
  note_en?: string;
  note_th?: string;
};

const MEDIA_TYPES = new Set<LessonMediaKind>(['pdf', 'image', 'html']);

function parseLessonContent(value: unknown): LessonMediaContent | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const type = typeof v.type === 'string' && MEDIA_TYPES.has(v.type as LessonMediaKind) ? (v.type as LessonMediaKind) : null;
  const files = Array.isArray(v.files) ? v.files.filter((f): f is string => typeof f === 'string' && f.trim().length > 0) : [];
  if (!type || files.length === 0) return null;
  return {
    type,
    files,
    note_en: typeof v.note_en === 'string' ? v.note_en : undefined,
    note_th: typeof v.note_th === 'string' ? v.note_th : undefined,
  };
}

function resolveLessonMediaUrl(path: string): string {
  if (/^(https?:|data:|blob:|\/)/.test(path)) return path;
  const objectPath = path.replace(/^lesson-media\//, '');
  if ((import.meta.env.VITE_DATA_SOURCE ?? 'mock') === 'supabase') {
    return getSupabase().storage.from('lesson-media').getPublicUrl(objectPath).data.publicUrl;
  }
  return `/lesson-media/${encodeURI(objectPath)}`;
}

/** Make an embedded handout fill the frame (drop the fixed A4 sheet) and grow to
 *  its content so the lesson page scrolls, not the iframe. Same-origin only. */
function fitLessonFrame(frame: HTMLIFrameElement) {
  try {
    const doc = frame.contentDocument;
    if (!doc) return;
    if (!doc.getElementById('wp-fit')) {
      const st = doc.createElement('style');
      st.id = 'wp-fit';
      st.textContent =
        'body{margin:0!important}' + /* keep the handout on its own light paper — readable under a dark org theme too */
        '.doc{padding:8px 10px 16px!important}' +
        '.page{width:auto!important;max-width:none!important;min-height:0!important;margin:0 0 14px!important;box-shadow:none!important;border-radius:0!important;padding:14px 16px!important}';
      doc.head.appendChild(st);
    }
    frame.style.height = `${doc.documentElement.scrollHeight + 8}px`;
  } catch {
    /* cross-origin — keep the fixed height */
  }
}

function isAnswered(q: QuizQuestion, answer: number | number[] | undefined): boolean {
  return q.type === 'multiple' ? Array.isArray(answer) && answer.length > 0 : answer != null;
}

function useResetOnLesson(reset: () => void, lessonId?: string) {
  useEffect(() => reset(), [lessonId, reset]);
}

export function LearnerLesson() {
  const { id = '', idx = '0' } = useParams();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { locale, L } = useLocalized();
  const { toast } = useToast();
  const { data: course } = useCourse(id);
  const i = Number(idx);
  const lesson = course?.lessons[i];
  const { data: quiz = [], isLoading: quizLoading } = useLessonQuiz(lesson?.id ?? '');
  const completeLesson = useCompleteLesson();
  const submitLessonQuiz = useSubmitLessonQuiz();
  const autoCompleted = useRef<Set<string>>(new Set());

  const [answers, setAnswers] = useState<QuizAnswers>({});
  const [quizResult, setQuizResult] = useState<LessonQuizResult | null>(null);
  const resetQuizState = useMemo(
    () => () => {
      setAnswers({});
      setQuizResult(null);
    },
    [],
  );
  useResetOnLesson(resetQuizState, lesson?.id);

  const content = useMemo(() => parseLessonContent(lesson?.content), [lesson?.content]);
  const mediaUrls = useMemo(() => content?.files.map((file) => ({ file, url: resolveLessonMediaUrl(file) })) ?? [], [content]);

  useEffect(() => {
    if (!lesson?.id || quizLoading || quiz.length > 0 || lesson.done || autoCompleted.current.has(lesson.id)) return;
    autoCompleted.current.add(lesson.id);
    completeLesson.mutate(lesson.id, {
      onError: () => autoCompleted.current.delete(lesson.id),
    });
  }, [completeLesson, lesson?.done, lesson?.id, quiz.length, quizLoading]);

  if (!course || !lesson) return <EmptyState icon="video" title="Lesson not found" />;

  const lessons = course.lessons;
  const total = lessons.length;
  const isLast = i === total - 1;
  const hasQuiz = quiz.length > 0;
  const isVideo = lesson.kind === 'video';
  const note = content ? (locale === 'th' ? content.note_th || content.note_en : content.note_en || content.note_th) : '';

  const doneCount = lessons.filter((l) => l.done).length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;
  const firstIncomplete = lessons.findIndex((l) => !l.done);
  const maxUnlocked = firstIncomplete === -1 ? total - 1 : firstIncomplete;
  const canContinue = !quizLoading && (!hasQuiz || !!quizResult || !!lesson.done);
  const quizReady = hasQuiz && quiz.every((q) => isAnswered(q, answers[q.id]));

  const goNext = () =>
    navigate(isLast ? `/learn/course/${id}/test/post` : `/learn/learning/${id}/lesson/${i + 1}`);
  const backToCourse = () => navigate(`/learn/catalog/${id}`);

  const pickAnswer = (q: QuizQuestion, opt: number) =>
    setAnswers((prev) => {
      if (q.type !== 'multiple') return { ...prev, [q.id]: opt };
      const current = Array.isArray(prev[q.id]) ? (prev[q.id] as number[]) : [];
      return { ...prev, [q.id]: current.includes(opt) ? current.filter((x) => x !== opt) : [...current, opt] };
    });

  const submitQuiz = async () => {
    if (!lesson?.id || !quizReady) return;
    try {
      const result = await submitLessonQuiz.mutateAsync({ lessonId: lesson.id, answers });
      setQuizResult(result);
      toast(`Check complete · ${result.correct}/${result.total} correct`, { icon: 'check' });
    } catch {
      toast('Could not submit quick check', { tone: 'clay', icon: 'x' });
    }
  };

  const emptyMaterial = (
    <div className="card" style={{ padding: '30px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: 'var(--ink-3)' }}>
      <Icon name={isVideo ? 'play' : 'doc'} size={24} style={{ color: 'var(--ink-4)' }} />
      <div style={{ fontSize: 14 }}>{L({ en: 'No material has been added to this lesson yet.', th: 'ยังไม่มีเนื้อหาสำหรับบทเรียนนี้' })}</div>
    </div>
  );

  const downloadLink = (url: string, label = 'Download') => (
    <a href={url} target="_blank" rel="noreferrer" download className="btn btn-ghost btn-sm">
      <Icon name="download" size={15} /> {label}
    </a>
  );

  const openHandoutButton = (url: string) => (
    <a href={url} target="_blank" rel="noreferrer" className="btn btn-primary btn-lg btn-block" style={{ textDecoration: 'none' }}>
      เปิดเอกสาร / Open handout <Icon name="arrowR" size={17} />
    </a>
  );

  const mediaDocument = content ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {note && (
        <div className={locale === 'th' ? 'card th' : 'card'} style={{ padding: '14px 16px', background: 'var(--card-2)', fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-2)' }}>
          {note}
        </div>
      )}
      {content.type === 'pdf' && mediaUrls.map(({ file, url }) => (
        <div key={file} className="card" style={{ padding: isDesktop ? 14 : 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {isDesktop ? (
            <object data={url} type="application/pdf" style={{ width: '100%', height: '75vh', border: '1px solid var(--line)', borderRadius: 10 }}>
              <iframe title={file} src={url} style={{ width: '100%', height: '75vh', border: 'none' }} />
            </object>
          ) : (
            openHandoutButton(url)
          )}
          {downloadLink(url, 'ดาวน์โหลด / Download')}
        </div>
      ))}
      {content.type === 'image' && mediaUrls.map(({ file, url }) => (
        <div key={file} className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <img src={url} alt="" style={{ maxWidth: '100%', borderRadius: 10, border: '1px solid var(--line)', display: 'block' }} />
          {downloadLink(url, 'ดาวน์โหลด / Download')}
        </div>
      ))}
      {content.type === 'html' && mediaUrls.map(({ file, url }) => (
        // Render the handout INLINE on mobile too (not a popped-out tab): on
        // mobile the separate tab's "close" exits the in-app browser and drops
        // the user out of the app. Inline keeps them on the lesson page with the
        // post-lesson quiz right below. Sandbox has no scripts, so any in-page
        // close button is inert.
        <div key={file} className="card" style={{ padding: isDesktop ? 14 : 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <iframe title={file} src={url} sandbox="allow-same-origin" onLoad={(e) => fitLessonFrame(e.currentTarget)} style={{ width: '100%', height: isDesktop ? 640 : 520, border: '1px solid var(--line)', borderRadius: 10, background: '#e7ecec', display: 'block' }} />
          {downloadLink(url, 'ดาวน์โหลด / Download')}
        </div>
      ))}
    </div>
  ) : null;

  const attachment = lesson.attachmentUrl ? (
    <a href={lesson.attachmentUrl} target="_blank" rel="noreferrer" className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', textDecoration: 'none', color: 'var(--ink)' }}>
      <span style={{ width: 34, height: 38, borderRadius: 6, background: 'var(--clay-soft)', color: 'var(--clay)', display: 'grid', placeItems: 'center', flex: 'none', fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600 }}>PDF</span>
      <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{lesson.attachmentLabel ?? 'Download handout'}</span>
      <Icon name="download" size={17} style={{ color: 'var(--ink-3)' }} />
    </a>
  ) : null;

  const legacyMedia = isVideo ? (
    lesson.videoUrl ? (
      <video controls src={lesson.videoUrl} style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 12, background: 'var(--ink)', display: 'block' }}>
        <track kind="captions" />
      </video>
    ) : (
      emptyMaterial
    )
  ) : lesson.body || lesson.attachmentUrl ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {lesson.body && (
        <div className="card" style={{ padding: isDesktop ? '20px 22px' : '16px 18px', whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.75, color: 'var(--ink-2)' }}>{L(lesson.body)}</div>
      )}
      {attachment}
    </div>
  ) : (
    emptyMaterial
  );

  const media = mediaDocument ?? legacyMedia;

  const hasKeyPoints = !!lesson.keyPoints?.length;
  const lessonContent = lesson.objective || hasKeyPoints ? (
    <div className="card" style={{ padding: '14px 16px', background: 'var(--card-2)', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {lesson.objective && (
        <div>
          <div className="eyebrow">Learning objective</div>
          <div style={{ fontSize: 14.5, color: 'var(--ink-2)', marginTop: 6, lineHeight: 1.5 }}>{L(lesson.objective)}</div>
        </div>
      )}
      {hasKeyPoints && (
        <div>
          <div className="eyebrow">Key points</div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 14.5, lineHeight: 1.7, color: 'var(--ink-2)' }}>
            {lesson.keyPoints!.map((kp, k) => <li key={k}>{L(kp)}</li>)}
          </ul>
        </div>
      )}
    </div>
  ) : null;

  const quizPanel = quizLoading ? (
    <div className="card" style={{ padding: 18, color: 'var(--ink-3)', fontSize: 14 }}>Loading quick check...</div>
  ) : hasQuiz ? (
    <div className="card" style={{ padding: isDesktop ? '20px 22px' : 16, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div className="eyebrow" style={{ color: 'var(--sage-deep)' }}>Quick check · <span className="th">ทวนความเข้าใจ</span></div>
        <div style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 4 }}>
          {quizResult || lesson.done ? 'Complete' : `${quiz.length} question${quiz.length === 1 ? '' : 's'} · submit to unlock the next lesson`}
        </div>
      </div>
      {quizResult || lesson.done ? (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 14, borderRadius: 12, background: 'var(--sage-tint)', color: 'var(--sage-deep)' }}>
            <Icon name="checkCircle" size={18} />
            <div style={{ fontSize: 14.5, fontWeight: 500 }}>
              {quizResult ? `Score ${quizResult.score}% · ${quizResult.correct}/${quizResult.total} correct` : 'Quick check already submitted'}
            </div>
          </div>
          {quizResult?.key && quiz.map((q) => (
            <div key={q.id} style={{ paddingTop: 16, borderTop: '1px solid var(--line)' }}>
              <QuizQuestionView q={q} selected={answers[q.id]} onPick={() => {}} correct={quizResult!.key![q.id]} />
            </div>
          ))}
        </>
      ) : (
        <>
          {quiz.map((q) => (
            <div key={q.id} style={{ paddingTop: 16, borderTop: '1px solid var(--line)' }}>
              <QuizQuestionView q={q} selected={answers[q.id]} onPick={(opt) => pickAnswer(q, opt)} />
            </div>
          ))}
          <button className="btn btn-primary btn-lg btn-block" disabled={!quizReady || submitLessonQuiz.isPending} onClick={submitQuiz}>
            {submitLessonQuiz.isPending ? 'Submitting...' : 'Submit quick check'} <Icon name="arrowR" size={17} />
          </button>
        </>
      )}
    </div>
  ) : null;

  const nextHint = hasQuiz
    ? 'Submit the quick check to unlock the next lesson.'
    : 'This lesson is recorded automatically when opened.';

  const footer = canContinue ? (
    <div style={{ flex: 'none', padding: '14px 24px 24px', background: 'linear-gradient(180deg, transparent, var(--paper) 32%)' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <button className="btn btn-primary btn-lg btn-block" disabled={completeLesson.isPending && !lesson.done} onClick={goNext}>
          {isLast ? 'Continue → Post-test' : 'บทเรียนถัดไป / Next lesson'} <Icon name="arrowR" size={17} />
        </button>
      </div>
    </div>
  ) : null;

  if (isDesktop) {
    return (
      <div style={{ display: 'flex', minHeight: 'calc(100dvh - 62px)' }}>
        <aside style={{ width: 280, flex: 'none', borderRight: '1px solid var(--line)', background: 'var(--card)', position: 'sticky', top: 62, height: 'calc(100dvh - 62px)', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 16px 14px', borderBottom: '1px solid var(--line)' }}>
            <button onClick={backToCourse} className="linkback">
              <Icon name="chevronL" size={16} /> Back to course
            </button>
            <div className="th" style={{ fontSize: 13.5, fontWeight: 600, marginTop: 8 }}>
              {course.title.th} · {course.code}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <Bar value={pct} style={{ flex: 1 }} />
              <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{doneCount} / {total} · {pct}%</span>
            </div>
          </div>
          <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
            {lessons.map((l: Lesson, j) => {
              const done = !!l.done;
              const cur = j === i;
              const locked = j > maxUnlocked && !cur;
              return (
                <button
                  key={l.id}
                  type="button"
                  disabled={locked}
                  onClick={() => !locked && !cur && navigate(`/learn/learning/${id}/lesson/${j}`)}
                  style={{ all: 'unset', display: 'flex', gap: 11, alignItems: 'center', padding: '11px 10px', borderRadius: 10, cursor: locked ? 'not-allowed' : cur ? 'default' : 'pointer', opacity: locked ? 0.5 : 1, background: cur ? 'var(--sage-tint)' : 'transparent' }}
                >
                  <span style={{ width: 24, height: 24, flex: 'none', borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 600, background: done ? 'var(--sage)' : cur ? 'var(--sage-soft)' : 'var(--paper-2)', color: done ? 'var(--on-accent)' : cur ? 'var(--sage-deep)' : 'var(--ink-3)' }}>
                    {done ? <Icon name="check" size={13} stroke={3} /> : locked ? <Icon name="lock" size={12} /> : j + 1}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: cur ? 600 : 500, color: 'var(--ink)' }}>{l.title.en}</div>
                    <small style={{ display: 'block', color: 'var(--ink-3)', fontSize: 11.5, marginTop: 1 }}>
                      {l.kind === 'video' ? 'Video' : 'Reading'} · {l.minutes} min{cur ? ' · in progress' : done ? ' · done' : ''}
                    </small>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '18px 28px 16px', borderBottom: '1px solid var(--line)', background: 'var(--paper)' }}>
            <div style={{ maxWidth: 1180, margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <span className="eyebrow">Step {i + 1} of {total} · {isVideo ? 'Video' : 'Reading'} · {lesson.minutes} min</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-2)' }}>
                  <i style={{ width: 8, height: 8, borderRadius: '50%', background: lesson.done ? 'var(--sage)' : 'var(--ochre)' }} />
                  {lesson.done ? 'Done' : 'In progress'}
                </span>
              </div>
              <h2 style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-.01em', margin: '8px 0 2px' }}>{lesson.title.en}</h2>
              <div className="th" style={{ fontSize: 14, color: 'var(--ink-3)' }}>{lesson.title.th}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--sage-deep)', marginTop: 10 }}>
                <Icon name="arrowR" size={16} style={{ color: 'var(--sage)' }} /> {nextHint}
              </div>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <div style={{ maxWidth: 1180, margin: '0 auto', padding: '22px 28px 8px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Handout fills the wide column; quiz + notes stay at a readable width. */}
              {media}
              <div style={{ width: '100%', maxWidth: 760, alignSelf: 'center', display: 'flex', flexDirection: 'column', gap: 20 }}>
                {lessonContent}
                {quizPanel}
              </div>
            </div>
          </div>
          {footer}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 30, background: 'var(--paper)', borderBottom: '1px solid var(--line)', padding: 'calc(env(safe-area-inset-top) + 12px) 16px 13px' }}>
        <button onClick={backToCourse} className="linkback">
          <Icon name="chevronL" size={16} /> {course.title.th} · {course.code}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 10 }}>
          <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', flex: 'none' }}>Lesson {i + 1} of {total}</span>
          <div style={{ display: 'flex', gap: 5, flex: 1 }}>
            {lessons.map((l) => (
              <i key={l.id} style={{ height: 5, borderRadius: 99, flex: 1, background: l.done ? 'var(--sage)' : l.id === lesson.id ? 'var(--sage-deep)' : 'var(--paper-2)' }} />
            ))}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, width: '100%', maxWidth: 640, margin: '0 auto', padding: '16px 20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {media}
        <div>
          <Badge kind={isVideo ? 'slate' : 'gray'}>{isVideo ? 'Video' : 'Reading'} · {lesson.minutes} min</Badge>
          <h2 style={{ fontSize: 21, fontWeight: 600, margin: '12px 0 4px', letterSpacing: '-.01em' }}>{lesson.title.en}</h2>
          <div className="th" style={{ fontSize: 14, color: 'var(--ink-3)' }}>{lesson.title.th}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--sage-deep)', marginTop: 10 }}>
            <Icon name="arrowR" size={16} style={{ color: 'var(--sage)' }} /> {nextHint}
          </div>
        </div>
        {lessonContent}
        {quizPanel}
      </div>

      {footer && <div style={{ position: 'sticky', bottom: 0 }}>{footer}</div>}
    </div>
  );
}
